import ReactDOM from "react-dom/client";
import { BrowserRouter, useNavigate } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ClerkProvider } from "@clerk/clerk-react";
import "@/index.css";
import App from "@/App";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000,
      refetchOnWindowFocus: false,
    },
  },
});

const clerkKey = process.env.REACT_APP_CLERK_PUBLISHABLE_KEY;

// BUG FIX (2026-09-21): ClerkProvider needs a `navigate` prop wired to the
// app's own router so its OWN top-level redirects (bouncing an
// already-signed-in visitor off /login, the post-sign-out redirect, its own
// session-sync retries, etc.) are ordinary in-app navigations. Without it,
// Clerk falls back to a hard `window.location` redirect for those -- a full
// page reload that tears down and rebuilds the whole React tree, including
// Clerk's own client, every single time. That is what produced the
// "reloading and not moving to dashboard" loop seen in production: each
// hard reload restarted Clerk's session bootstrap from scratch, and nothing
// stopped it from reloading again immediately. Routing this through React
// Router turns it into an instant, in-app transition instead, so a hiccup
// in Clerk's own session sync (e.g. the backend briefly returning 401) can
// no longer show up as an uncontrolled full-page reload storm.
//
// This requires BrowserRouter to be mounted OUTSIDE ClerkProvider (Clerk
// needs router context to call useNavigate()) -- see App.js, which no
// longer creates its own BrowserRouter.
function ClerkProviderWithRoutes({ children }) {
  const navigate = useNavigate();
  return (
    <ClerkProvider
      publishableKey={clerkKey}
      navigate={(to) => navigate(to)}
      signInUrl="/login"
      signInFallbackRedirectUrl="/"
      afterSignOutUrl="/login"
    >
      {children}
    </ClerkProvider>
  );
}

// Clerk is optional: wrap only when a publishable key is configured, otherwise
// the app runs with the legacy email/password auth (local-first fallback).
const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <BrowserRouter>
    <QueryClientProvider client={queryClient}>
      {clerkKey ? (
        <ClerkProviderWithRoutes>
          <App />
        </ClerkProviderWithRoutes>
      ) : (
        <App />
      )}
    </QueryClientProvider>
  </BrowserRouter>,
);
